import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { useDispatch } from "react-redux";
import { addRoles, updateRoles } from "../../actions/userActions/roleAction";
import {
  getAllPermissionsGroup
} from "../../actions/userActions/permissionsAction";
import LoadingOverlay from "../loadingOverlay/LoadingOverlay";
import roleicon from "../../assets/images/roleicon.svg";
import { IconButton } from "@mui/material";
import closeModal from "../../assets/images/closeModal.svg";
import MultiSelect from "../designSystem/MultiSelect";
import "../DesignationLevel/DynamicModal.css"
import Textarea from "../designSystem/TextArea";
import TextField from "../designSystem/TextField";
import { validateFields } from "../../utils/Validations";
import { Check } from "lucide-react";
import BodySmallMedium from "../designSystem/BodySmallMedium";
import CancelButton from '../subComponents/buttons/CustomCancelButton';
import PrimaryButton from '../subComponents/buttons/PrimaryButton';

import AddNewForm from "../addNew/AddNewForm";
import AddNewDrawer from "../addNew/AddNewDrawer";
import permissionIcon from "../../assets/images/Icon=Permissions.svg";
import { SIZE_OF_PAGE } from "../../actions/types";

import { t } from "../../locales/translate";

function AddRoles({ onClose, editData, onSave }) {
  const dispatch = useDispatch();
  const initialValues = {
    name: "",
    description: "",
    roleGroup: [],
    active: false,
  };
  const [loading, setLoading] = useState(true);
  const [formValues, setFormValues] = useState(initialValues);
  const [formErrors, setFormErrors] = useState({});
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerItem, setDrawerItem] = useState(null);
  const [drawerLoading, setDrawerLoading] = useState(false);

  const [groupOptions, setGroupOptions] = useState([]);
  const [labelMap, setLabelMap] = useState({});
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  const currentPageRef = useRef(1);
  const currentSearchRef = useRef("");
  const searchTimeoutRef = useRef(null);
  const loadingMoreRef = useRef(false);

  const mapGroupsToOptions = (groups = []) =>
    groups
      .filter((group) => group.active)
      .map((group) => ({
        value: group.id,
        label: group.name,
        // Lets MultiSelect's local filter keep API matches (name or description)
        subtext: group.description || "",
      }));

  const mergeLabels = useCallback((options) => {
    setLabelMap((prev) => {
      const next = { ...prev };
      options.forEach((option) => {
        next[option.value] = option.label;
      });
      return next;
    });
  }, []);

  const loadGroups = useCallback(async (searchText, page, type) => {
    if (type === "more") {
      if (loadingMoreRef.current) return;
      loadingMoreRef.current = true;
      setIsLoadingMore(true);
    } else if (type === "search") {
      setIsSearching(true);
    }

    try {
      const data = await dispatch(
        getAllPermissionsGroup(page, SIZE_OF_PAGE, searchText || "")
      );
      const result = data?.result || [];
      const newOptions = mapGroupsToOptions(result);

      currentPageRef.current = page;
      setHasMore(result.length === SIZE_OF_PAGE);
      mergeLabels(newOptions);

      if (page === 1) {
        // Force MultiSelect session refresh (it only updates when options.length changes)
        flushSync(() => {
          setGroupOptions([]);
        });
        setGroupOptions(newOptions);
      } else {
        setGroupOptions((prev) => {
          const existingIds = new Set(prev.map((option) => option.value));
          const unique = newOptions.filter((option) => !existingIds.has(option.value));
          return [...prev, ...unique];
        });
      }
    } finally {
      if (type === "more") {
        loadingMoreRef.current = false;
        setIsLoadingMore(false);
      } else if (type === "search") {
        setIsSearching(false);
      }
    }
  }, [dispatch, mergeLabels]);

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      setLoading(true);
      currentSearchRef.current = "";
      currentPageRef.current = 1;
      setGroupOptions([]);
      setHasMore(true);

      try {
        if (editData) {
          const roleGroupIds = (editData.permissionGroup || []).map((item) => item.id);
          const pinnedLabels = {};
          (editData.permissionGroup || []).forEach((item) => {
            pinnedLabels[item.id] = item.name;
          });
          if (isMounted) {
            setLabelMap(pinnedLabels);
            setFormValues({
              ...initialValues,
              name: editData.name,
              description: editData.description,
              id: editData.id,
              active: editData.active,
              roleGroup: roleGroupIds,
            });
          }
        } else if (isMounted) {
          setLabelMap({});
          setFormValues(initialValues);
        }

        await loadGroups("", 1, "search");
      } catch (error) {
        if (isMounted) console.error("Error fetching data:", error);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchData();
    return () => {
      isMounted = false;
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [editData, loadGroups]);

  const visibleOptions = useMemo(() => {
    const selectedIds = formValues.roleGroup || [];
    const selectedIdSet = new Set(selectedIds);

    const selectedOnTop = selectedIds
      .filter((id) => labelMap[id] !== undefined)
      .map((id) => ({
        value: id,
        label: labelMap[id],
      }));

    const rest = groupOptions.filter((option) => !selectedIdSet.has(option.value));
    return [...selectedOnTop, ...rest];
  }, [formValues.roleGroup, groupOptions, labelMap]);

  const loadNextPage = useCallback(() => {
    if (!hasMore || isLoadingMore || isSearching || loading) return;
    loadGroups(currentSearchRef.current, currentPageRef.current + 1, "more");
  }, [hasMore, isLoadingMore, isSearching, loading, loadGroups]);

  const handleSearch = useCallback((inputValue) => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    currentSearchRef.current = inputValue;
    searchTimeoutRef.current = setTimeout(() => {
      loadGroups(inputValue, 1, "search");
    }, 300);
  }, [loadGroups]);

  const handleMenuOpen = useCallback(() => {
    if (currentSearchRef.current !== "") {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
      currentSearchRef.current = "";
      currentPageRef.current = 1;
      loadGroups("", 1, "search");
    }
  }, [loadGroups]);

  const handletextChange = (event) => {
    const { name, value } = event.target;
    setFormValues({
      ...formValues,
      [name]: value,
    });
  };

  const handleAddNew = (itemName) => {
    setDrawerItem(itemName);
    setDrawerOpen(true);
  };

  const handleChange = (e) => {
    const { name, checked, type, value } = e.target;
    setFormValues((prevValues) => ({
      ...prevValues,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const errors = validateFields(formValues, "role");
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setLoading(true);
    try {
      if (editData) {
        await dispatch(updateRoles(formValues));
      } else {
        await dispatch(addRoles(formValues));
      }
      setLoading(false);
      onSave(formValues);
    } catch (error) {
      console.error("Error saving role:", error);
      setLoading(false);
    }
  };

  const handlePermissionGroupChange = (updatedValues) => {
    setFormValues((prevState) => ({
      ...prevState,
      roleGroup: updatedValues,
    }));
  };

  return (
    <>
      <div className="dynamicmodal-overlay">
        <div className="dynamicmodal-container overlay-wrapper">
          <LoadingOverlay isVisible={loading} />
          <div className={loading ? "blur" : ""}>
            <div className="dynamicmodal-header mb-3">
              <div className="dynamicmodal-header-left">
                <div className="icon">
                  <img src={roleicon} alt=" roleicon" />
                </div>
                <div className="text">
                  <h2 className="modalheading">
                    {editData ? "Edit Role" : `${t("addRole")}`}
                  </h2>
                  <p className="modaldescription">
                    {t("addRoleTip")}
                  </p>
                </div>
              </div>
              <div className="dynamicmodal-header-right">
                <IconButton onClick={onClose}>
                  <img src={closeModal} alt="" />
                </IconButton>
              </div>
            </div>
            <form onSubmit={handleSave}>
              <div className="dynamicmodal-body">
                <div className="dynamicmodal-form-group">
                  <TextField
                    name="name"
                    label={`${t("name")} *`}
                    type="text"
                    value={formValues.name}
                    onChange={handletextChange}
                    placeholder={t("name")}
                    error={formErrors.name}
                  />
                </div>

                <div className="dynamicmodal-form-group">
                  <MultiSelect
                    isSearchable
                    onChange={handlePermissionGroupChange}
                    value={formValues?.roleGroup || []}
                    label={`${t("permissionGroup")} *`}
                    error={formErrors.roleGroup}
                    options={visibleOptions}
                    configKey={"permissionGroup"}
                    onAddNewClick={() => handleAddNew("permissionGroup")}
                    onInputChange={handleSearch}
                    onMenuOpen={handleMenuOpen}
                    onScrollBottom={loadNextPage}
                    scrollLoadOffset={140}
                    isLoadingMore={isLoadingMore}
                    isLoading={isSearching}
                  />
                </div>
                <Textarea
                  name="description"
                  label={`${t("description")} *`}
                  placeholder={t("description")}
                  value={formValues.description}
                  onChange={handletextChange}
                  error={formErrors.description}
                />
                <div className=" mt-2 ml-0">
                  <div
                    className="form-group"
                    style={{ display: "flex", alignItems: "flex-start" }}
                  >
                    <input
                      name="active"
                      style={{ cursor: "pointer", height: "14px", width: "14px" }}
                      type="checkbox"
                      checked={formValues.active}
                      className="ml-1 mt-1"
                      onChange={handleChange}
                    />
                    <label
                      style={{ paddingLeft: "5px" }}
                      className="form-control-label"
                    >
                      <div style={{ padding: "2px" }}>
                        <BodySmallMedium text={t("active")} color="#1E293B" />
                      </div>
                    </label>
                  </div>
                </div>
              </div>

              <div className="dynamicmodal-footer">
                <CancelButton
                  onClick={onClose}
                  text={t("cancel")}
                  width={document.body.dir === "rtl" ? "119px" : ""}
                />
                <PrimaryButton
                  text={t("save")}
                  icon={Check}
                  width={document.body.dir === "rtl" ? "123px" : ""}
                />
              </div>
            </form>
          </div>
        </div>
      </div>

      <AddNewDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={drawerItem ? t("addItem").replace(/{{item}}/g, t(drawerItem)) : ""}
        subtext={drawerItem ? t("createNewItemRecord").replace(/{{item}}/g, t(drawerItem)) : ""}
        icon={drawerItem === "permissionGroup" ? <img src={permissionIcon} alt={drawerItem} /> : null}
        loading={drawerLoading}
      >
        {drawerItem && (
          <AddNewForm
            itemName={drawerItem}
            onClose={() => setDrawerOpen(false)}
            onLoadingChange={setDrawerLoading}
            onSave={(newData) => {
              if (drawerItem === "permissionGroup") {
                if (newData?.id != null) {
                  setLabelMap((prev) => ({
                    ...prev,
                    [newData.id]: newData.name,
                  }));
                  setFormValues((prev) => ({
                    ...prev,
                    roleGroup: [...(prev.roleGroup || []), newData.id],
                  }));
                }
                currentSearchRef.current = "";
                currentPageRef.current = 1;
                loadGroups("", 1, "search");
              }
              setDrawerOpen(false);
            }}
          />
        )}
      </AddNewDrawer>
    </>
  );
}

export default AddRoles;
